/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   Mon Jun 11 2019 10:54:38 AM
 *
 *******************************************************/
//flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import { getAppAdminFeatures } from 'src/helpers/features-helper';

import apiService from 'src/services/api-service';
import {
  AUTO_SETUP_THEME_REQUEST,
  AUTO_SETUP_THEME_SUCCESS,
  AUTO_SETUP_THEME_FAILURE,
  SETUP_FEEDBACK_REQUEST,
  SETUP_FEEDBACK_SUCCESS,
  SETUP_FEEDBACK_FAILURE,
  GET_UPGRADE_THEME_STATUS_REQUEST,
  GET_UPGRADE_THEME_STATUS_SUCCESS,
  GET_UPGRADE_THEME_STATUS_FAILURE,
  RESET_SETUP_FEEDBACK,
  RESET_UPGRADE_THEME_STATUS
} from 'stores/flow-type/setup.type';

/**
 * Call to duplicate theme and install app
 * @return {object} The RSAA object for API middleware
 */
export const autoSetupTheme = (body: Object, themeSetupV2Default = false, i18n: I18n) => {
  const themeSetupV2 = themeSetupV2Default || getAppAdminFeatures('themeSetupV2');

  const route = themeSetupV2
    ? 'admin-v3.theme-setup.auto-setup-theme'
    : 'admin-v2.setup-requests.auto-setup-theme';

  return apiService.sendRequest(route, { body }, [
    AUTO_SETUP_THEME_REQUEST,
    {
      type: AUTO_SETUP_THEME_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: AUTO_SETUP_THEME_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Installation.Actions.autoSetupTheme'),
          i18n
        )
    }
  ]);
};

/**
 * Submit feedback after installation
 * @return {object} The RSAA object for API middleware
 */
export const sendSetupFeedback = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.setup-requests.feedback', { body }, [
    SETUP_FEEDBACK_REQUEST,
    {
      type: SETUP_FEEDBACK_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: SETUP_FEEDBACK_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Installation.Actions.sendSetupFeedback'),
          i18n
        )
    }
  ]);
};

/**
 * Reset setup feedback state
 * @return {object} The RSAA object for API middleware
 */
export const resetSetupFeedback = () => {
  return {
    type: RESET_SETUP_FEEDBACK,
    payload: {}
  };
};

/**
 * Call to get status of theme upgrade
 * @return {object} The RSAA object for API middleware
 */
export const getUpgradeThemeStatus = (i18n: I18n) => {
  const route = 'admin-v3.theme-setup.upgrade-theme-status';
  return apiService.sendRequest(route, {}, [
    GET_UPGRADE_THEME_STATUS_REQUEST,
    {
      type: GET_UPGRADE_THEME_STATUS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: GET_UPGRADE_THEME_STATUS_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.Installation.Actions.getUpgradeThemeStatus'),
          i18n
        )
    }
  ]);
};

/**
 * Reset upgrade theme status
 * @return {object} The RSAA object for API middleware
 */
export const resetUpgradeThemeStatus = () => {
  return {
    type: RESET_UPGRADE_THEME_STATUS,
    payload: {}
  };
};
